import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, streamChat, API, getToken } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ChevronLeft, Download, Send, Sparkles, BookOpen, MessageSquare, Mic, MicOff, FileText, Trash2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { createRecognizer, isSpeechSupported } from "../lib/speech.js";
import ReadAloudButton from "../components/ReadAloudButton.jsx";
import { useAuth } from "../lib/auth.jsx";

export default function ChapterDetail() {
  const { chapterId } = useParams();
  const [chapter, setChapter] = useState(null);
  const [tab, setTab] = useState("notes");

  useEffect(() => {
    api.get(`/chapters/${chapterId}`).then((r) => setChapter(r.data));
  }, [chapterId]);

  if (!chapter) return <div className="text-brimi-navyText/60">Loading chapter…</div>;
  const isHistory = chapter.subject === "history";
  const accentBg = isHistory ? "bg-brimi-terracotta/15" : "bg-brimi-sage/15";
  const accentText = isHistory ? "text-brimi-terracotta" : "text-brimi-sage";

  return (
    <div className="space-y-6">
      <Link
        to={`/subject/${chapter.subject}/${chapter.class}`}
        className="inline-flex items-center gap-1 text-sm text-brimi-navyText/70 hover:text-brimi-navy"
        data-testid="back-subject"
      >
        <ChevronLeft size={16} /> Back to chapters
      </Link>
      <div>
        <span className={`brimi-pill ${accentBg} ${accentText}`}>
          Class {chapter.class === "9" ? "IX" : "X"} · {chapter.section}
        </span>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-brimi-navyText mt-2">{chapter.title}</h1>
        <p className="text-brimi-navyText/70 mt-1">{chapter.outline}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <div className="overflow-x-auto -mx-1 px-1 pb-1 scrollbar-thin">
          <TabsList className="bg-brimi-creamSoft p-1 rounded-xl inline-flex w-max">
            <TabsTrigger value="notes" data-testid="tab-notes" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm whitespace-nowrap">
              <BookOpen size={14} className="mr-1.5" /> Notes
            </TabsTrigger>
            <TabsTrigger value="mcq" data-testid="tab-mcq" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm whitespace-nowrap">
              <Sparkles size={14} className="mr-1.5" /> Practice
            </TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm whitespace-nowrap">
              <MessageSquare size={14} className="mr-1.5" /> AI Tutor
            </TabsTrigger>
            <TabsTrigger value="resources" data-testid="tab-resources" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm whitespace-nowrap">
              <FileText size={14} className="mr-1.5" /> Resources
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="notes" className="mt-5">
          <NotesPanel chapter={chapter} />
        </TabsContent>
        <TabsContent value="mcq" className="mt-5">
          <MCQPanel chapter={chapter} />
        </TabsContent>
        <TabsContent value="chat" className="mt-5">
          <ChatPanel chapter={chapter} />
        </TabsContent>
        <TabsContent value="resources" className="mt-5">
          <ResourcesPanel chapter={chapter} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function NotesPanel({ chapter }) {
  const [notes, setNotes] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/chapters/${chapter.id}/notes`);
      setNotes(data.notes);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter.id]);

  const downloadPDF = () => {
    if (!notes) return;
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const margin = 48;
    const maxWidth = 595 - margin * 2;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.text(chapter.title, margin, margin);
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(110);
    pdf.text(`ICSE 2028 · Class ${chapter.class === "9" ? "IX" : "X"} · ${chapter.section}`, margin, margin + 18);
    pdf.setTextColor(20);
    pdf.setFontSize(11);
    const plain = notes.replace(/[#*_`>]/g, "").replace(/\n{3,}/g, "\n\n");
    const lines = pdf.splitTextToSize(plain, maxWidth);
    let y = margin + 48;
    lines.forEach((ln) => {
      if (y > 800) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(ln, margin, y);
      y += 15;
    });
    pdf.save(`${chapter.title.replace(/[^a-z0-9]/gi, "_")}.pdf`);
  };

  return (
    <div className="brimi-card p-6 md:p-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-xl font-heading font-semibold text-brimi-navyText">AI-generated chapter notes</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {notes && <ReadAloudButton text={notes} label="Read aloud" testid="notes-tts-btn" />}
          <button onClick={downloadPDF} disabled={!notes} className="brimi-btn-ghost" data-testid="download-pdf-btn">
            <Download size={16} /> Download PDF
          </button>
        </div>
      </div>
      {loading && <p className="text-brimi-navyText/60">Generating focused notes for you…</p>}
      {!loading && notes && (
        <article className="markdown-body text-brimi-navyText/90" data-testid="notes-content">
          <ReactMarkdown>{notes}</ReactMarkdown>
        </article>
      )}
    </div>
  );
}

function MCQPanel({ chapter }) {
  const [qs, setQs] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    setSubmitted(false);
    setAnswers({});
    try {
      const { data } = await api.post("/mcq/generate", { chapter_id: chapter.id, count: 10 });
      setQs(data.questions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate MCQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter.id]);

  const submit = async () => {
    const score = qs.reduce((acc, q, i) => acc + (answers[i] === q.answer_index ? 1 : 0), 0);
    setSubmitted(true);
    try {
      await api.post("/mcq/submit", { chapter_id: chapter.id, score, total: qs.length });
      toast.success(`Scored ${score}/${qs.length}`);
    } catch {
      /* ignore */
    }
  };

  const score = qs.reduce((acc, q, i) => acc + (answers[i] === q.answer_index ? 1 : 0), 0);

  return (
    <div className="brimi-card p-6 md:p-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-xl font-heading font-semibold text-brimi-navyText">Practice MCQs</h2>
        <button onClick={generate} className="brimi-btn-ghost" data-testid="mcq-regen-btn">
          <Sparkles size={16} /> New set
        </button>
      </div>
      {loading && <p className="text-brimi-navyText/60">Cooking up fresh questions…</p>}
      {!loading && qs.length === 0 && <p className="text-brimi-navyText/60">No questions yet.</p>}
      <div className="space-y-6">
        {qs.map((q, i) => (
          <div key={i} className="border-t border-brimi-creamSoft pt-5 first:border-none first:pt-0" data-testid={`mcq-q-${i}`}>
            <p className="font-medium text-brimi-navyText">
              <span className="text-brimi-navyText/50">{i + 1}.</span> {q.q}
            </p>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {q.options.map((opt, oi) => {
                const isPicked = answers[i] === oi;
                const isAnswer = q.answer_index === oi;
                const cls = submitted
                  ? isAnswer
                    ? "bg-brimi-sage/15 border-brimi-sage/40 text-brimi-navyText"
                    : isPicked
                    ? "bg-brimi-terracotta/15 border-brimi-terracotta/40 text-brimi-navyText"
                    : "border-brimi-creamSoft text-brimi-navyText/80"
                  : isPicked
                  ? "bg-brimi-navy/10 border-brimi-navy/30 text-brimi-navyText"
                  : "border-brimi-creamSoft hover:border-brimi-navy/30 text-brimi-navyText/85";
                return (
                  <button
                    key={oi}
                    onClick={() => !submitted && setAnswers((a) => ({ ...a, [i]: oi }))}
                    className={`text-left px-4 py-2.5 rounded-xl border transition ${cls}`}
                    data-testid={`mcq-q-${i}-opt-${oi}`}
                  >
                    <span className="font-mono text-xs text-brimi-navyText/50 mr-2">{String.fromCharCode(65 + oi)}</span>
                    {opt}
                  </button>
                );
              })}
            </div>
            {submitted && q.explanation && (
              <p className="text-sm text-brimi-navyText/70 mt-2 bg-brimi-creamSoft/60 rounded-lg px-3 py-2">
                <strong>Explanation:</strong> {q.explanation}
              </p>
            )}
          </div>
        ))}
      </div>
      {qs.length > 0 && (
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3 border-t border-brimi-creamSoft pt-5">
          <div className="text-brimi-navyText/70 text-sm">
            {Object.keys(answers).length}/{qs.length} answered
          </div>
          {!submitted ? (
            <button
              onClick={submit}
              disabled={Object.keys(answers).length < qs.length}
              className="brimi-btn-primary"
              data-testid="mcq-submit-btn"
            >
              Submit answers
            </button>
          ) : (
            <div className="text-brimi-navyText font-heading font-semibold text-lg" data-testid="mcq-score">
              Score: {score}/{qs.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChatPanel({ chapter }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const sessionIdRef = useRef(`chat-${chapter.id}-${Date.now()}`);
  const bottomRef = useRef(null);
  const recogRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const toggleMic = () => {
    if (listening) {
      recogRef.current?.stop();
      return;
    }
    const rec = createRecognizer({
      onResult: ({ combined }) => setInput(combined),
      onEnd: (finalText) => {
        setListening(false);
        if (finalText) setInput((cur) => (cur && cur !== finalText ? cur : finalText));
      },
      onError: (err) => {
        setListening(false);
        if (err && err !== "aborted" && err !== "no-speech") toast.error(`Mic: ${err}`);
      },
    });
    if (!rec) {
      toast.error("Voice input isn't supported in this browser. Try Chrome on desktop or Android.");
      return;
    }
    recogRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (e) {
      toast.error("Could not start microphone");
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      await streamChat({
        chapter_id: chapter.id,
        session_id: sessionIdRef.current,
        message: text,
        onDelta: (d) =>
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + d };
            return copy;
          }),
        onDone: () => setBusy(false),
        onError: (e) => {
          toast.error(e.message);
          setBusy(false);
        },
      });
    } catch (e) {
      toast.error("Chat failed");
      setBusy(false);
    }
  };

  const speechSupported = isSpeechSupported();

  return (
    <div className="brimi-card p-0 overflow-hidden">
      <div className="p-5 border-b border-brimi-creamSoft">
        <h2 className="text-xl font-heading font-semibold text-brimi-navyText">Ask the AI tutor</h2>
        <p className="text-sm text-brimi-navyText/65">
          Focused on this chapter. Powered by Claude. {speechSupported && "Tap the mic to speak."}
        </p>
      </div>
      <div className="h-[420px] overflow-y-auto p-5 space-y-4" data-testid="chat-scroll">
        {messages.length === 0 && (
          <div className="text-sm text-brimi-navyText/55 text-center py-10">
            Try: <span className="italic">{`"Explain Ashoka's Dhamma in 4 points"`}</span>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className="flex flex-col gap-1 max-w-[85%]">
              <div
                className={`rounded-2xl px-4 py-3 ${
                  m.role === "user"
                    ? "bg-brimi-navy text-white"
                    : "bg-brimi-creamSoft text-brimi-navyText markdown-body"
                }`}
                data-testid={`chat-msg-${i}`}
              >
                {m.role === "assistant" ? <ReactMarkdown>{m.content || "…"}</ReactMarkdown> : m.content}
              </div>
              {m.role === "assistant" && m.content && (
                <div className="flex justify-start">
                  <ReadAloudButton text={m.content} compact testid={`chat-tts-${i}`} />
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="p-3 sm:p-4 border-t border-brimi-creamSoft flex gap-2 bg-white">
        {speechSupported && (
          <button
            type="button"
            onClick={toggleMic}
            disabled={busy}
            className={`flex-shrink-0 rounded-xl px-3 transition ${
              listening ? "bg-brimi-terracotta text-white" : "bg-brimi-creamSoft text-brimi-navyText hover:bg-brimi-cream"
            }`}
            title={listening ? "Stop listening" : "Voice input"}
            data-testid="chat-mic-btn"
            aria-pressed={listening}
          >
            {listening ? <MicOff size={16} /> : <Mic size={16} />}
          </button>
        )}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={listening ? "Listening…" : "Ask about this chapter…"}
          className="flex-1 min-w-0 rounded-xl border border-brimi-creamSoft bg-white px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-brimi-navy/30"
          data-testid="chat-input"
          disabled={busy}
        />
        <button
          onClick={send}
          disabled={busy || !input.trim()}
          className="brimi-btn-primary flex-shrink-0 !px-3 sm:!px-5"
          data-testid="chat-send-btn"
          aria-label="Send"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}

function ResourcesPanel({ chapter }) {
  const { user } = useAuth();
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/owner/pdfs", { params: { chapter_id: chapter.id } });
      setPdfs(data.pdfs || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter.id]);

  const upload = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) {
      toast.error("Choose a PDF file first");
      return;
    }
    if (f.type !== "application/pdf") {
      toast.error("Only PDFs are supported");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("title", title || f.name.replace(/\.pdf$/i, ""));
      fd.append("chapter_id", chapter.id);
      await api.post("/owner/pdfs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF uploaded");
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this PDF?")) return;
    try {
      await api.delete(`/owner/pdfs/${id}`);
      setPdfs((ps) => ps.filter((p) => p.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const downloadHref = (id) => `${API}/owner/pdfs/${id}/download?auth=${encodeURIComponent(getToken() || "")}`;

  return (
    <div className="brimi-card p-6 md:p-8 space-y-6">
      <div>
        <h2 className="text-xl font-heading font-semibold text-brimi-navyText">Resources & PDFs</h2>
        <p className="text-sm text-brimi-navyText/65 mt-1">
          Supplementary PDFs for this chapter uploaded by the BrimiH&G team.
        </p>
      </div>

      {user?.is_owner && (
        <form onSubmit={upload} className="rounded-xl border border-brimi-creamSoft bg-brimi-cream/50 p-4 space-y-3" data-testid="owner-upload-form">
          <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/60">Owner upload</p>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional — defaults to filename)"
            className="w-full rounded-xl border border-brimi-creamSoft bg-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-brimi-navy/30 text-sm"
            data-testid="owner-pdf-title-input"
          />
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            className="w-full text-sm text-brimi-navyText/80 file:mr-3 file:rounded-xl file:border-0 file:bg-brimi-navy file:text-white file:px-4 file:py-2 file:cursor-pointer"
            data-testid="owner-pdf-file-input"
          />
          <button type="submit" disabled={uploading} className="brimi-btn-primary" data-testid="owner-pdf-upload-btn">
            {uploading ? "Uploading…" : "Upload PDF"}
          </button>
        </form>
      )}

      {loading && <p className="text-brimi-navyText/60 text-sm">Loading resources…</p>}
      {!loading && pdfs.length === 0 && (
        <p className="text-brimi-navyText/60 text-sm" data-testid="resources-empty">
          No supplementary resources for this chapter yet.
        </p>
      )}
      {!loading && pdfs.length > 0 && (
        <ul className="space-y-2" data-testid="resources-list">
          {pdfs.map((p) => (
            <li key={p.id} className="flex items-center gap-3 border border-brimi-creamSoft rounded-xl p-3" data-testid={`resource-${p.id}`}>
              <div className="w-10 h-10 rounded-xl bg-brimi-terracotta/15 text-brimi-terracotta grid place-items-center">
                <FileText size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-brimi-navyText truncate">{p.title}</p>
                <p className="text-xs text-brimi-navyText/60">
                  {p.original_filename} · {(p.size / 1024).toFixed(0)} KB
                </p>
              </div>
              <a
                href={downloadHref(p.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="brimi-btn-ghost text-sm"
                data-testid={`resource-open-${p.id}`}
              >
                <Download size={14} /> Open
              </a>
              {user?.is_owner && (
                <button
                  onClick={() => remove(p.id)}
                  className="p-2 rounded-lg text-brimi-navyText/50 hover:bg-brimi-creamSoft hover:text-brimi-terracotta"
                  title="Delete"
                  data-testid={`resource-delete-${p.id}`}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
